import React, { useEffect, useState } from "react";
import { View, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Platform } from "react-native";
import { useRoute, useNavigation } from "@react-navigation/native";
import type { NavigationProp } from "@react-navigation/native";
import { MotiView } from "moti";
import {
  ArrowLeft,
  ArrowsClockwise,
  Download,
  CheckCircle,
  Warning,
  Lightbulb,
  Star,
  BookOpen,
  ClipboardText,
  Check,
  RadioButton,
  WarningCircle,
  ArrowCircleUp,
  FileText,
  TrendUp,
} from "@phosphor-icons/react";

import { useGetLatestCourseEvaluationQuery, useEvaluateCourseMutation } from "../../graphql/generated/graphql";

import AdminLayout from "../../layouts/AdminLayout.web";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import type { RootStackParamList } from "../../navigation/navigation.types";
import { formatDateWithTime, formatSemester } from "../../utils/date";
import AssignmentAccordion from "../../components/adminDashboard/AssignmentAccordion/AssignmentAccordion";

// Badge component for effectiveness ratings
const EffectivenessBadge = ({ rating }: { rating: string }) => {
  const getBadgeStyle = () => {
    switch (rating?.toLowerCase()) {
      case "excellent":
        return { bg: "#ECFDF3", text: "#027A48", border: "#A6F4C5" };
      case "good":
        return { bg: "#EEF4FF", text: "#3538CD", border: "#C7D7FE" };
      case "fair":
        return { bg: "#FFFAEB", text: "#B54708", border: "#FEDF89" };
      case "poor":
        return { bg: "#FEF3F2", text: "#B42318", border: "#FECDCA" };
      default:
        return { bg: "#F2F4F7", text: "#344054", border: "#E4E7EC" };
    }
  };

  const style = getBadgeStyle();

  return (
    <View style={[styles.effectivenessBadge, { backgroundColor: style.bg, borderColor: style.border }]}>
      <CustomText useTranslationText={false} text={rating} size="xs" weight="medium" style={{ color: style.text }} />
    </View>
  );
};

// Curriculum Evaluation Badge with different styling
const CurriculumEvaluationBadge = ({ rating }: { rating: string }) => {
  const getBadgeStyle = () => {
    switch (rating?.toLowerCase()) {
      case "good":
        return { bg: "rgb(239, 246, 255)", text: "rgb(29, 78, 216)", border: "rgb(191, 219, 254)" };
      default:
        return { bg: "rgb(239, 246, 255)", text: "rgb(29, 78, 216)", border: "rgb(191, 219, 254)" };
    }
  };

  const style = getBadgeStyle();

  return (
    <View style={[styles.curriculumEvaluationBadge, { backgroundColor: style.bg, borderColor: style.border }]}>
      <CustomText useTranslationText={false} text={rating} size="s" weight="bold" style={{ color: style.text }} />
    </View>
  );
};

// Assignment Evaluation Badge with larger styling
const AssignmentEvaluationBadge = ({ rating }: { rating: string }) => {
  const getBadgeStyle = () => {
    switch (rating?.toLowerCase()) {
      case "excellent":
        return { bg: "#ECFDF3", text: "#027A48", border: "#A6F4C5" };
      case "good":
        return { bg: "#EEF4FF", text: "#3538CD", border: "#C7D7FE" };
      case "fair":
        return { bg: "#FFFAEB", text: "#B54708", border: "#FEDF89" };
      case "poor":
        return { bg: "#FEF3F2", text: "#B42318", border: "#FECDCA" };
      default:
        return { bg: "#F2F4F7", text: "#344054", border: "#E4E7EC" };
    }
  };

  const style = getBadgeStyle();

  return (
    <View style={[styles.assignmentEvaluationBadge, { backgroundColor: style.bg, borderColor: style.border }]}>
      <CustomText useTranslationText={false} text={rating} size="s" weight="bold" style={{ color: style.text }} />
    </View>
  );
};

// Recommendation item with checkbox
const RecommendationItem = ({ text, type }: { text: string; type: "critical" | "recommended" | "enhancement" }) => {
  const [checked, setChecked] = useState(false);

  const getTypeColor = () => {
    switch (type) {
      case "critical":
        return "#991b1b";
      case "recommended":
        return "#92400e";
      case "enhancement":
        return "#1e40af";
    }
  };

  return (
    <View style={styles.recommendationItem}>
      <TouchableOpacity
        style={[styles.checkbox, checked && { backgroundColor: getTypeColor(), borderColor: getTypeColor() }]}
        onPress={() => setChecked(!checked)}>
        {checked && <Check size={12} color="#fff" weight="bold" />}
      </TouchableOpacity>
      <CustomText
        useTranslationText={false}
        text={text}
        size="s"
        weight="normal"
        style={[styles.recommendationText, { color: getTypeColor() }]}
      />
    </View>
  );
};

function CourseEvaluatorDetailView() {
  const route = useRoute();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const { courseId } = route.params as { courseId: string };

  const { data, loading, refetch } = useGetLatestCourseEvaluationQuery({
    variables: { courseId },
    fetchPolicy: "network-only",
  });

  const evaluation = data?.getLatestCourseEvaluation;
  const [evaluateCourse, { loading: evaluating }] = useEvaluateCourseMutation();
  const isPending = evaluation?.status === "pending";

  const handleEvaluation = async () => {
    try {
      await evaluateCourse({ variables: { courseId } });
      refetch();
    } catch (err) {
      console.log("Error starting evaluation", err);
    }
  };

  useEffect(() => {
    if (!evaluation || evaluation.status !== "pending") return;
    const interval = setInterval(() => {
      refetch();
    }, 2000);
    return () => clearInterval(interval);
  }, [evaluation, refetch]);

  const curriculum = evaluation?.curriculumEvaluationJson ? JSON.parse(evaluation.curriculumEvaluationJson) : null;
  const assignmentEvaluation = evaluation?.assignmentEvaluationJson
    ? JSON.parse(evaluation.assignmentEvaluationJson)
    : null;

  const hasCompletedEvaluation = evaluation?.status === "completed" && curriculum && assignmentEvaluation;

  const formatHeaderMeta = (): string => {
    const parts: string[] = [];

    // Add course code if available
    if (evaluation?.courseHasEvaluation?.course_code) {
      parts.push(evaluation.courseHasEvaluation.course_code);
    }

    // Add semester if startDate is available
    const course = evaluation?.courseHasEvaluation as { startDate?: string | null } | null | undefined;
    const semester = formatSemester(course?.startDate);
    if (semester) {
      parts.push(semester);
    }

    // Add evaluated date with time if available
    if (evaluation?.createdAt) {
      parts.push(`Evaluated on ${formatDateWithTime(evaluation.createdAt)}`);
    }

    return parts.join(" · ");
  };

  // Group recommendations by severity
  const getRecommendations = () => {
    const recs = evaluation?.teacherRecommendations || [];
    // For now, distribute recommendations across categories
    const critical = recs.slice(0, Math.ceil(recs.length / 3));
    const recommended = recs.slice(Math.ceil(recs.length / 3), Math.ceil((recs.length * 2) / 3));
    const enhancement = recs.slice(Math.ceil((recs.length * 2) / 3));
    return { critical, recommended, enhancement };
  };

  const recommendations = getRecommendations();
  const totalRecommendations =
    recommendations.critical.length + recommendations.recommended.length + recommendations.enhancement.length;

  // Get assignment stats
  const getAssignmentStats = () => {
    const analysis = assignmentEvaluation?.assignmentAnalysis || [];
    const stats = { excellent: 0, good: 0, fair: 0, poor: 0 };
    analysis.forEach((a: any) => {
      const eff = a.effectiveness?.toLowerCase();
      if (eff === "excellent") stats.excellent++;
      else if (eff === "good") stats.good++;
      else if (eff === "fair") stats.fair++;
      else if (eff === "poor") stats.poor++;
    });
    return { total: analysis.length, ...stats };
  };

  const assignmentStats = getAssignmentStats();

  return (
    <AdminLayout withPadding={false}>
      <ScrollView style={styles.pageBackground} tabIndex={0}>
        <View style={styles.contentContainer}>
          {/* Back button */}
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.navigate("Course Evaluation List")}>
            <ArrowLeft size={16} color="#667085" />
            <CustomText
              useTranslationText={false}
              text="Back to Course Evaluator"
              size="s"
              weight="medium"
              style={styles.backButtonText}
            />
          </TouchableOpacity>

          <CustomSpacing type="vertical" size="xl" />

          {/* Header */}
          <MotiView from={{ opacity: 0, translateY: 20 }} animate={{ opacity: 1, translateY: 0 }}>
            <View style={styles.headerSection}>
              <View style={styles.headerLeft}>
                <CustomText
                  useTranslationText={false}
                  text={evaluation?.courseHasEvaluation?.name || "Course Evaluation"}
                  size="l"
                  textType="display"
                  weight="ultraBold"
                  style={styles.pageTitle}
                />
                <CustomText useTranslationText={false} text={formatHeaderMeta()} size="m" style={styles.headerMeta} />
              </View>
              <View style={styles.headerActions}>
                <TouchableOpacity style={styles.secondaryButton}>
                  <Download size={16} color="#344054" />
                  <CustomText
                    useTranslationText={false}
                    text="Export"
                    size="s"
                    weight="medium"
                    style={styles.buttonText}
                  />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.secondaryButton}
                  onPress={handleEvaluation}
                  disabled={evaluating || isPending}>
                  {evaluating || isPending ? (
                    <ActivityIndicator color="#344054" size="small" />
                  ) : (
                    <>
                      <ArrowsClockwise size={16} color="#344054" />
                      <CustomText
                        useTranslationText={false}
                        text={evaluation ? "Re-evaluate" : "Evaluate"}
                        size="s"
                        weight="medium"
                        style={styles.buttonText}
                      />
                    </>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          </MotiView>

          <CustomSpacing type="vertical" size="xl" />

          {/* Loading state */}
          {loading && (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#7F56D9" />
              <CustomSpacing type="vertical" size="s" />
              <CustomText useTranslationText={false} text="Loading evaluation..." size="m" style={styles.loadingText} />
            </View>
          )}

          {/* Pending state */}
          {isPending && (
            <View style={styles.pendingCard}>
              <ActivityIndicator size="small" color="#7F56D9" />
              <CustomText
                useTranslationText={false}
                text="Evaluation is being generated..."
                size="m"
                weight="medium"
                style={styles.pendingText}
              />
            </View>
          )}

          {/* No evaluation state */}
          {!loading && !evaluation && (
            <View style={styles.emptyCard}>
              <ClipboardText size={48} color="#D0D5DD" />
              <CustomSpacing type="vertical" size="m" />
              <CustomText
                useTranslationText={false}
                text="No evaluation yet"
                size="m"
                weight="semiBold"
                style={styles.emptyTitle}
              />
              <CustomText
                useTranslationText={false}
                text="Click the Evaluate button to generate an AI-powered course evaluation"
                size="s"
                style={styles.emptyDescription}
              />
            </View>
          )}

          {/* Completed evaluation */}
          {hasCompletedEvaluation && (
            <>
              {/* Overall Summary Card */}
              <MotiView from={{ opacity: 0, translateY: 20 }} animate={{ opacity: 1, translateY: 0 }}>
                <View style={styles.card}>
                  <View style={styles.cardHeader}>
                    <View
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 20,
                        backgroundColor: "rgb(230, 235, 255)",
                        justifyContent: "center",
                        alignItems: "center",
                      }}>
                      <RadioButton size={24} color="rgb(62, 104, 254)" />
                    </View>
                    <CustomText
                      useTranslationText={false}
                      text="Overall Summary"
                      size="xl"
                      weight="bold"
                      style={styles.cardTitle}
                    />
                  </View>

                  {/* Effectiveness badges */}
                  <View style={styles.badgeRow}>
                    <View style={styles.badgeGroup}>
                      <CustomText useTranslationText={false} text="Curriculum:" size="s" style={styles.badgeLabel} />
                      <EffectivenessBadge rating={curriculum.effectiveness || "N/A"} />
                    </View>

                    <View
                      style={{
                        width: 1,
                        height: 20,
                        backgroundColor: "rgb(234, 236, 240)",
                      }}
                    />

                    <View style={styles.badgeGroup}>
                      <CustomText useTranslationText={false} text="Assignments:" size="s" style={styles.badgeLabel} />
                      <EffectivenessBadge rating={assignmentEvaluation.effectiveness || "N/A"} />
                    </View>
                  </View>

                  {/* Recommendations section */}
                  {totalRecommendations > 0 && (
                    <>
                      <View style={styles.recommendationsSection}>
                        <View style={styles.recommendationsHeader}>
                          <View style={styles.recommendationsTitle}>
                            <Lightbulb size={16} color="#F79009" />
                            <CustomText
                              useTranslationText={false}
                              text="Recommendations"
                              size="s"
                              weight="bold"
                              style={styles.recommendationsTitleText}
                            />
                          </View>
                          <CustomText
                            useTranslationText={false}
                            text={`0 of ${totalRecommendations} addressed`}
                            size="xs"
                            style={styles.recommendationsCount}
                          />
                        </View>

                        <View
                          style={{
                            height: 5,
                            backgroundColor: "#E4E7EC",
                            borderRadius: 2.5,
                          }}
                        />

                        <CustomSpacing type="vertical" size="xxl" />

                        {/* Critical recommendations */}
                        {recommendations.critical.length > 0 && (
                          <View style={[styles.recommendationGroup, styles.criticalGroup]}>
                            <View style={styles.recommendationGroupHeader}>
                              <WarningCircle size={18} color="#B42318" />
                              <CustomText
                                useTranslationText={false}
                                text={`Critical (${recommendations.critical.length})`}
                                size="s"
                                weight="bold"
                                style={{ color: "#991b1b" }}
                              />
                            </View>
                            {recommendations.critical.map((rec, idx) => (
                              <RecommendationItem key={idx} text={rec} type="critical" />
                            ))}
                          </View>
                        )}

                        {/* Recommended */}
                        {recommendations.recommended.length > 0 && (
                          <View style={[styles.recommendationGroup, styles.recommendedGroup]}>
                            <View style={styles.recommendationGroupHeader}>
                              <Lightbulb size={18} color="#B54708" />
                              <CustomText
                                useTranslationText={false}
                                text={`Recommended (${recommendations.recommended.length})`}
                                size="s"
                                weight="bold"
                                style={{ color: "#92400e" }}
                              />
                            </View>
                            {recommendations.recommended.map((rec, idx) => (
                              <RecommendationItem key={idx} text={rec} type="recommended" />
                            ))}
                          </View>
                        )}

                        {/* Enhancement */}
                        {recommendations.enhancement.length > 0 && (
                          <View style={[styles.recommendationGroup, styles.enhancementGroup]}>
                            <View style={styles.recommendationGroupHeader}>
                              <ArrowCircleUp size={18} color="#3538CD" />
                              <CustomText
                                useTranslationText={false}
                                text={`Enhancement (${recommendations.enhancement.length})`}
                                size="s"
                                weight="bold"
                                style={{ color: "#1e40af" }}
                              />
                            </View>
                            {recommendations.enhancement.map((rec, idx) => (
                              <RecommendationItem key={idx} text={rec} type="enhancement" />
                            ))}
                          </View>
                        )}
                      </View>
                    </>
                  )}
                </View>
              </MotiView>

              {/* Curriculum Evaluation Card */}
              <MotiView from={{ opacity: 0, translateY: 20 }} animate={{ opacity: 1, translateY: 0 }}>
                <View style={styles.card}>
                  <View style={styles.cardHeaderWithBadge}>
                    <View style={styles.cardHeaderLeft}>
                      <View
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 20,
                          backgroundColor: "rgb(250, 245, 255)",
                          justifyContent: "center",
                          alignItems: "center",
                        }}>
                        <BookOpen size={24} color="#7F56D9" />
                      </View>
                      <View>
                        <CustomText
                          useTranslationText={false}
                          text="Curriculum Evaluation"
                          size="xl"
                          weight="bold"
                          style={styles.cardTitle}
                        />
                        <CustomText
                          useTranslationText={false}
                          text="Analysis of curriculum completeness and essential concept coverage"
                          size="s"
                          style={styles.cardSubtitle}
                        />
                      </View>
                    </View>
                    <CurriculumEvaluationBadge rating={curriculum.effectiveness || "N/A"} />
                  </View>

                  {/* Concept Coverage */}
                  {curriculum.conceptCoverage && (
                    <View style={styles.subsection}>
                      <View style={styles.subsectionHeader}>
                        <FileText size={16} color="#667085" weight="regular" />
                        <CustomText
                          useTranslationText={false}
                          text="Concept Coverage"
                          size="s"
                          weight="bold"
                          style={styles.subsectionTitle}
                        />
                      </View>
                      <View style={styles.conceptCoverageContainer}>
                        <CustomText
                          useTranslationText={false}
                          text={curriculum.conceptCoverage}
                          size="s"
                          weight="normal"
                          style={styles.subsectionText}
                        />
                      </View>
                      <CustomSpacing type="vertical" size="l" />
                    </View>
                  )}

                  {/* Strengths */}
                  {curriculum.strengths && curriculum.strengths.length > 0 && (
                    <View style={styles.subsection}>
                      <View style={styles.subsectionHeader}>
                        <Star size={16} color="#12B76A" weight="regular" />
                        <CustomText
                          useTranslationText={false}
                          text="Strengths"
                          size="s"
                          weight="bold"
                          style={styles.subsectionTitle}
                        />
                      </View>
                      <View style={styles.strengthsGrid}>
                        {curriculum.strengths.map((item: string, idx: number) => (
                          <View key={idx} style={styles.strengthItem}>
                            <CheckCircle size={20} color="rgb(22, 163, 74)" weight="regular" />
                            <CustomText useTranslationText={false} text={item} size="s" style={styles.strengthText} />
                          </View>
                        ))}
                      </View>
                      <CustomSpacing type="vertical" size="l" />
                    </View>
                  )}

                  {/* Improvement Suggestions */}
                  {curriculum.suggestions && curriculum.suggestions.length > 0 && (
                    <View style={styles.subsection}>
                      <View style={styles.subsectionHeader}>
                        <Lightbulb size={16} color="#3b83f6" weight="regular" />
                        <CustomText
                          useTranslationText={false}
                          text="Improvement Suggestions"
                          size="s"
                          weight="bold"
                          style={styles.subsectionTitle}
                        />
                      </View>
                      {curriculum.suggestions.map((item: string, idx: number) => (
                        <View
                          key={idx}
                          style={[
                            styles.suggestionListItem,
                            idx === curriculum.suggestions.length - 1 && styles.suggestionListItemLast,
                          ]}>
                          <TrendUp size={20} color="rgb(30, 64, 175)" weight="regular" />
                          <CustomText
                            useTranslationText={false}
                            text={item}
                            size="s"
                            style={styles.suggestionItemText}
                          />
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              </MotiView>

              {/* Assignment Evaluation Card */}
              <MotiView from={{ opacity: 0, translateY: 20 }} animate={{ opacity: 1, translateY: 0 }}>
                <View style={styles.card}>
                  <View style={styles.cardHeaderWithBadge}>
                    <View style={styles.cardHeaderLeft}>
                      <View
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 20,
                          backgroundColor: "rgb(255, 247, 237)",
                          justifyContent: "center",
                          alignItems: "center",
                        }}>
                        <ClipboardText size={24} color="rgb(234, 88, 12)" />
                      </View>
                      <View>
                        <CustomText
                          useTranslationText={false}
                          text="Assignment Evaluation"
                          size="xl"
                          weight="bold"
                          style={styles.cardTitle}
                        />
                        <CustomText
                          useTranslationText={false}
                          text="Evaluation of how well assignments gauge understanding and cover concepts"
                          size="s"
                          style={styles.cardSubtitle}
                        />
                      </View>
                    </View>
                    <AssignmentEvaluationBadge rating={assignmentEvaluation.effectiveness || "N/A"} />
                  </View>

                  {/* Assignment stats */}
                  <View style={styles.assignmentStatsContainer}>
                    <View style={styles.assignmentStatsRow}>
                      <CustomText
                        useTranslationText={false}
                        text={`${assignmentStats.total} assignments evaluated`}
                        size="s"
                        weight="medium"
                        style={styles.assignmentStatsLabel}
                      />
                      <View style={styles.statsBreakdown}>
                        {assignmentStats.good > 0 && (
                          <View style={[styles.statsBadge, { backgroundColor: "#EEF4FF" }]}>
                            <CustomText
                              useTranslationText={false}
                              text={`${assignmentStats.good} Good`}
                              size="xs"
                              weight="medium"
                              style={{ color: "#3538CD" }}
                            />
                          </View>
                        )}
                        {assignmentStats.fair > 0 && (
                          <View style={[styles.statsBadge, { backgroundColor: "#FFFAEB" }]}>
                            <CustomText
                              useTranslationText={false}
                              text={`${assignmentStats.fair} Fair`}
                              size="xs"
                              weight="medium"
                              style={{ color: "#B54708" }}
                            />
                          </View>
                        )}
                        {assignmentStats.excellent > 0 && (
                          <View style={[styles.statsBadge, { backgroundColor: "#ECFDF3" }]}>
                            <CustomText
                              useTranslationText={false}
                              text={`${assignmentStats.excellent} Excellent`}
                              size="xs"
                              weight="medium"
                              style={{ color: "#027A48" }}
                            />
                          </View>
                        )}
                      </View>
                    </View>

                    {assignmentEvaluation.conceptCoverageByAssignments && (
                      <CustomText
                        useTranslationText={false}
                        text={assignmentEvaluation.conceptCoverageByAssignments}
                        size="s"
                        style={styles.assignmentCoverageText}
                      />
                    )}
                  </View>

                  {/* Assignment strengths */}
                  {assignmentEvaluation.strengths && assignmentEvaluation.strengths.length > 0 && (
                    <View style={styles.assignmentStrengthsContainer}>
                      <View style={styles.assignmentStrengthsGrid}>
                        {assignmentEvaluation.strengths.map((item: string, idx: number) => (
                          <View key={idx} style={styles.assignmentStrengthItem}>
                            <CheckCircle size={20} color="rgb(22, 163, 74)" weight="regular" />
                            <CustomText useTranslationText={false} text={item} size="s" style={styles.strengthText} />
                          </View>
                        ))}
                      </View>
                    </View>
                  )}

                  {/* Assignment list */}
                  {assignmentEvaluation.assignmentAnalysis && assignmentEvaluation.assignmentAnalysis.length > 0 && (
                    <View style={styles.assignmentList}>
                      {assignmentEvaluation.assignmentAnalysis.map((analysis: any) => (
                        <AssignmentAccordion
                          key={analysis.assignmentId || analysis.title}
                          analysis={{ ...analysis, courseCode: evaluation?.courseHasEvaluation?.course_code }}
                        />
                      ))}
                    </View>
                  )}
                </View>
              </MotiView>
            </>
          )}
        </View>
      </ScrollView>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  pageBackground: {
    flex: 1,
    backgroundColor: "#F9FAFB",
  },
  contentContainer: {
    padding: 32,
    maxWidth: 1024,
    width: "100%",
    alignSelf: "center",
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  backButtonText: {
    color: "#475467",
  },
  headerSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLeft: {
    flex: 1,
  },
  pageTitle: {
    color: "#101828",
    fontSize: 24,
    lineHeight: 32,
  },
  headerMeta: {
    color: "#475467",
    marginTop: 4,
  },
  headerActions: {
    flexDirection: "row",
    gap: 12,
  },
  secondaryButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: "#fff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#D0D5DD",
  },
  buttonText: {
    color: "#344054",
  },
  primaryButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: "#7F56D9",
    borderRadius: 8,
    minWidth: 120,
    justifyContent: "center",
  },
  primaryButtonText: {
    color: "#fff",
  },
  loadingContainer: {
    padding: 60,
    alignItems: "center",
    justifyContent: "center",
  },
  loadingText: {
    color: "#667085",
  },
  pendingCard: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    padding: 20,
    backgroundColor: "#F9F5FF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E9D7FE",
  },
  pendingText: {
    color: "#7F56D9",
  },
  emptyCard: {
    padding: 60,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E4E7EC",
    borderStyle: "dashed",
  },
  emptyTitle: {
    color: "#344054",
  },
  emptyDescription: {
    color: "#667085",
    textAlign: "center",
    marginTop: 4,
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E4E7EC",
    padding: 24,
    marginBottom: 20,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
  },
  cardHeaderWithBadge: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  cardHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flex: 1,
  },
  cardTitle: {
    color: "#101828",
  },
  cardSubtitle: {
    color: "#475467",
    marginTop: 2,
  },
  badgeRow: {
    flexDirection: "row",
    gap: 24,
    marginBottom: 20,
  },
  badgeGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  badgeLabel: {
    color: "#475467",
  },
  effectivenessBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: 1,
  },
  statsBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  curriculumEvaluationBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 9999,
    borderWidth: 1,
  },
  assignmentEvaluationBadge: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 9999,
    borderWidth: 1,
  },
  recommendationsSection: {
    paddingTop: 0,
  },
  recommendationsHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  recommendationsTitle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  recommendationsTitleText: {
    color: "#344054",
  },
  recommendationsCount: {
    color: "#667085",
  },
  recommendationGroup: {
    padding: 16,
    borderRadius: 8,
    marginBottom: 12,
    borderWidth: 1,
  },
  criticalGroup: {
    backgroundColor: "#FEF3F2",
    borderColor: "rgb(254, 202, 202)",
  },
  recommendedGroup: {
    backgroundColor: "#FFFAEB",
    borderColor: "rgb(253, 230, 138)",
  },
  enhancementGroup: {
    backgroundColor: "#EEF4FF",
    borderColor: "rgb(191, 219, 254)",
  },
  recommendationGroupHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  recommendationItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    marginBottom: 8,
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#D0D5DD",
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 2,
  },
  recommendationText: {
    flex: 1,
    lineHeight: 20,
  },
  subsection: {
    marginBottom: 12,
  },
  subsectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  subsectionTitle: {
    color: "#344054",
  },
  subsectionText: {
    color: "#475467",
    lineHeight: 22,
    ...(Platform.OS === "web"
      ? ({
          whiteSpace: "pre-line",
        } as any)
      : {}),
  },
  conceptCoverageContainer: {
    backgroundColor: "rgb(249, 250, 251)",
    padding: 16,
    borderRadius: 8,
  },
  strengthsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  strengthItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    backgroundColor: "rgb(240, 253, 244)",
    borderColor: "rgb(187, 247, 208)",
    borderWidth: 1,
    borderRadius: 16,
    padding: 12,
    flex: 1,
    minWidth: "48%",
    ...(Platform.OS === "web"
      ? ({
          maxWidth: "calc(50% - 2px)",
        } as any)
      : {}),
  },
  strengthIconCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "transparent",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "rgb(22, 163, 74)",
  },
  strengthText: {
    color: "rgb(22, 101, 52)",
    flex: 1,
    lineHeight: 20,
  },
  suggestionListItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    marginBottom: 10,
    backgroundColor: "rgb(239, 246, 255)",
    borderColor: "rgb(191, 219, 254)",
    borderWidth: 1,
    borderRadius: 16,
    padding: 12,
  },
  suggestionListItemLast: {
    marginBottom: 0,
  },
  suggestionNumber: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "#F79009",
    alignItems: "center",
    justifyContent: "center",
  },
  suggestionNumberText: {
    color: "#fff",
  },
  suggestionItemText: {
    color: "rgb(30, 64, 175)",
    flex: 1,
    lineHeight: 20,
  },
  assignmentStatsContainer: {
    backgroundColor: "rgb(249, 250, 251)",
    padding: 16,
    borderRadius: 8,
    marginBottom: 20,
  },
  assignmentStatsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  assignmentStatsLabel: {
    color: "#344054",
  },
  statsBreakdown: {
    flexDirection: "row",
    gap: 16,
  },
  assignmentCoverageText: {
    color: "#475467",
    lineHeight: 22,
  },
  assignmentList: {
    gap: 12,
  },
  assignmentStrengthsContainer: {
    marginBottom: 20,
  },
  assignmentStrengthsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  assignmentStrengthItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    backgroundColor: "rgb(240, 253, 244)",
    borderColor: "rgb(187, 247, 208)",
    borderWidth: 1,
    borderRadius: 16,
    padding: 12,
  },
});

export default CourseEvaluatorDetailView;
